import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Popover, Box, Button, SxProps, Theme } from '@mui/material';
import BasicTextField from 'components/basic-text-field';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

interface TimeRangePickerProps {
  startTime: string | null;
  endTime: string | null;
  onChange: (start: string, end: string) => void;
  required?: boolean;
  startHelperText?: string;
  endHelperText?: string;
}

const sxStyles: { [key: string]: SxProps<Theme> } = {
  popoverContent: {
    p: 2,
    display: 'flex',
    gap: 2,
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: 400
  }
};

const formatDisplayTime = (time: string | null): string =>
  time ? moment(time, 'HH:mm').format('hh:mm A') : '';

export const TimeRangePicker = ({
  startTime,
  endTime,
  onChange,
  required,
  startHelperText,
  endHelperText
}: TimeRangePickerProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempStartTime, setTempStartTime] = useState(startTime);
  const [tempEndTime, setTempEndTime] = useState(endTime);
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);

  useEffect(() => {
    if (required) {
      setStartError(startHelperText ? true : false);
      setEndError(endHelperText ? true : false);
    }
  }, [startHelperText, endHelperText]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setTempStartTime(startTime);
    setTempEndTime(endTime);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSave = () => {
    onChange(tempStartTime || '', tempEndTime || '');
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <BasicTextField
        fullWidth
        labelText={t('organization.timeRange')}
        value={`${formatDisplayTime(startTime)} - ${formatDisplayTime(endTime)}`}
        onClick={handleOpen}
        required={required}
        error={startError || endError}
        helperText={startError ? startHelperText : endError ? endHelperText : ''}
        readOnly
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={sxStyles.popoverContent}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              label="From"
              value={tempStartTime ? moment(tempStartTime, 'HH:mm') : null}
              onChange={(newValue: moment.MomentInput) => {
                const val = newValue ? moment(newValue).format('HH:mm') : '';
                setTempStartTime(val);
              }}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
            <TimePicker
              label="To"
              value={tempEndTime ? moment(tempEndTime, 'HH:mm') : null}
              onChange={(newValue: moment.MomentInput) => {
                const val = newValue ? moment(newValue).format('HH:mm') : '';
                setTempEndTime(val);
              }}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
          <Button variant="outlined" onClick={handleSave}>
            {t('done')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};
